import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import {
  LayoutDashboard,
  Building2,
  CalendarDays,
  ListTree,
  GanttChart,
  ClipboardList,
  GitCompare,
  Sparkles,
  Bell,
  Upload,
  LogOut,
  User,
  FolderKanban,
  Lock,
  Menu,
  ChevronRight,
  History,
  Mail,
  Shield,
  Settings,
  Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import ActiveProjectHeader from '../components/project/ActiveProjectHeader';
import AIAssistantWidget from '../components/ai/AIAssistantWidget';
import ErrorBoundary from '../components/ErrorBoundary';
import api from '../lib/api';
import { GLOBAL_NAV, SCHEDULE_NAV, ANALYSIS_NAV, OPERATIONS_NAV, SETTINGS_NAV } from '../lib/navigation';
import { useIsMobile } from '../hooks/useIsMobile';
import wbsLogo from '../assets/logos/wbs.png';
import activityLogo from '../assets/logos/activity.png';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  // `can(action)` is bound to the org axis (user.role) + the caller's role on the
  // ACTIVE project (activeProjectRole), mirroring backend rbac.py. Project-scoped
  // nav visibility is decided by it; the admin-portal section stays on the org axis.
  const { activeProject, activeSchedule, can } = useProject();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const schedulingPaths = ['/scheduling', '/activities', '/wbs', '/gantt'];
  const showProjectSelector = schedulingPaths.some(p => location.pathname.startsWith(p));
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Mobile (<md): the sidebar is an off-canvas drawer toggled by a header
  // hamburger. Desktop (>=md) keeps the fixed sidebar unchanged (md: overrides).
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Fetch notification count
  useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        const response = await api.get('/notifications/count');
        setUnreadCount(response.data.unread_count);
      } catch (error) {
        console.error('Failed to fetch notification count:', error);
      }
    };

    fetchNotificationCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close the mobile drawer whenever the route changes (so tapping a nav link
  // navigates AND dismisses the overlay). No-op on desktop.
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // When the viewport becomes mobile, clear any desktop "collapsed" state — the
  // mobile sidebar is a full-width drawer. Otherwise a sidebar collapsed on
  // desktop, then resized to mobile, would open as an unexpandable 16px icon
  // strip (the logo-tap expand is a no-op on mobile).
  useEffect(() => {
    if (isMobile) setSidebarCollapsed(false);
  }, [isMobile]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatRole = (role) => {
    if (!role) return '';
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Icon lookup for navigation items defined in lib/navigation.js
  const ICON_MAP = {
    LayoutDashboard, Building2, CalendarDays, ListTree, GanttChart,
    ClipboardList, GitCompare, Sparkles, Bell, Upload, FolderKanban,
    History, Mail, Shield, Package
  };

  // Image-icon lookup. Mirrors ICON_MAP but for nav items declared as iconType: 'image'.
  const IMAGE_MAP = {
    wbs: wbsLogo,
    activity: activityLogo,
  };

  // Resolve each nav item's icon spec into a renderable handle attached to the item.
  // Lucide-based items get `.icon` (component); image-based items get `.imgSrc` (string).
  const withIcons = (items) => items.map(item => (
    item.iconType === 'image'
      ? { ...item, imgSrc: IMAGE_MAP[item.iconKey] }
      : { ...item, icon: ICON_MAP[item.iconName] }
  ));

  // Single render helper for either icon type so the four NavItem variants stay in sync.
  const renderIcon = (item, sizeClass) => (
    item.iconType === 'image'
      ? <img src={item.imgSrc} alt="" className={`${sizeClass} object-contain`} />
      : <item.icon className={sizeClass} />
  );

  const globalNav = withIcons(GLOBAL_NAV);
  const scheduleNav = withIcons(SCHEDULE_NAV);
  const analysisNav = withIcons(ANALYSIS_NAV);
  const operationsNav = withIcons(OPERATIONS_NAV);
  const settingsNav = withIcons(SETTINGS_NAV);

  // `hasAccess` is decided by the caller: global items are always accessible;
  // project-scoped items pass can(item.action) against the active project's role.
  // Keeping the access decision out of NavItem preserves every collapsed/expanded
  // + lock-icon UX branch unchanged while swapping only the policy source.
  const NavItem = ({ item, hasAccess }) => {
    // Collapsed state - restricted access
    if (!hasAccess && sidebarCollapsed) {
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-300 cursor-not-allowed"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-restricted`}
              >
                {renderIcon(item, 'h-5 w-5')}
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={12} className="text-xs">
              <p>{item.label} - Access restricted</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    
    // Expanded state - restricted access
    if (!hasAccess) {
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-300 cursor-not-allowed"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-restricted`}
              >
                {renderIcon(item, 'h-4 w-4 flex-shrink-0')}
                <span className="flex-1">{item.label}</span>
                <Lock className="h-3 w-3" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs">
              <p>Access restricted for your role</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    // Collapsed state - has access
    if (sidebarCollapsed) {
      return (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <NavLink
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                    isActive
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`
                }
              >
                {renderIcon(item, 'h-5 w-5')}
              </NavLink>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={12} className="text-xs">
              <p>{item.label}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    // Expanded state - has access
    return (
      <NavLink
        to={item.to}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            isActive
              ? 'bg-slate-900 text-white'
              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          }`
        }
      >
        {renderIcon(item, 'h-4 w-4')}
        {item.label}
      </NavLink>
    );
  };

  // `resolveAccess(item) => boolean` lets each section supply its own policy:
  // the global section is always accessible; project sections pass can(item.action).
  const NavSection = ({ title, items, collapsible = false, resolveAccess = () => true }) => {
    const [isOpen, setIsOpen] = useState(true);

    return (
      <div className={sidebarCollapsed ? 'flex flex-col items-center w-full' : 'space-y-1'}>
        {title && !sidebarCollapsed && (
          collapsible ? (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between px-3 mb-2 group cursor-pointer"
              data-testid={`nav-section-${title.toLowerCase()}`}
            >
              <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 group-hover:text-slate-600 transition-colors">
                {title}
              </p>
              <ChevronRight
                className={`h-3 w-3 text-slate-400 group-hover:text-slate-600 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
              />
            </button>
          ) : (
            <p className="px-3 text-[10px] uppercase tracking-widest font-semibold text-slate-400 mb-2">
              {title}
            </p>
          )
        )}
        {(!collapsible || isOpen || sidebarCollapsed) && items.map((item) => (
          <NavItem key={item.to} item={item} hasAccess={resolveAccess(item)} />
        ))}
      </div>
    );
  };

  // Project-scoped access: the active project's role decides each item via can().
  // For admin/super_admin (no membership row) can(READ) is false for project work
  // — exactly the backend's 404/403, so the items lock. super_admin short-circuits
  // to true inside can(), so it sees everything.
  const resolveProjectAccess = (item) => can(item.action);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-layout">
      {/* Mobile drawer backdrop — only below md, only when the drawer is open */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          data-testid="mobile-sidebar-backdrop"
          aria-hidden="true"
        />
      )}
      {/* Sidebar — fixed on desktop; off-canvas drawer below md */}
      <aside
        className={`fixed left-0 top-0 z-40 h-screen border-r border-slate-200 bg-white flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'} ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        data-testid="sidebar"
      >
        {/* Logo Header */}
        <div 
          className={`flex h-14 items-center border-b border-slate-200 ${sidebarCollapsed ? 'justify-center' : 'px-4'}`}
          onClick={() => { if (!isMobile) setSidebarCollapsed(!sidebarCollapsed); }}
          style={{ cursor: 'pointer' }}
          data-testid="sidebar-header"
        >
          {sidebarCollapsed ? (
            /* Collapsed: Show only logo icon */
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="h-9 w-9 rounded-md bg-slate-900 flex items-center justify-center">
                    <GanttChart className="h-5 w-5 text-white" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8} className="text-xs">
                  <p>Expand sidebar</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            /* Expanded: Show logo with text */
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-slate-900 flex items-center justify-center flex-shrink-0">
                <GanttChart className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-base font-semibold tracking-tight text-slate-900">
                  epicplan
                </h1>
                <p className="text-[10px] text-slate-400 -mt-0.5">Create Epic Plans</p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <ScrollArea className={`flex-1 ${sidebarCollapsed ? 'py-3' : 'py-4 px-3'}`}>
          <nav className={`flex flex-col ${sidebarCollapsed ? 'items-center gap-1 px-[13px]' : 'gap-6'}`} data-testid="sidebar-nav">
            {/* Global navigation - always visible */}
            <NavSection items={globalNav} />

            {/* Project-specific sections - only on scheduling pages with an active project */}
            {activeProject && showProjectSelector && (
              <>
                {sidebarCollapsed ? (
                  <div className="w-8 h-px bg-slate-200 my-2" />
                ) : (
                  <Separator className="bg-slate-100" />
                )}
                <NavSection title="Schedule" items={scheduleNav} collapsible resolveAccess={resolveProjectAccess} />
                {sidebarCollapsed ? (
                  <div className="w-8 h-px bg-slate-200 my-2" />
                ) : (
                  <Separator className="bg-slate-100" />
                )}
                <NavSection title="Analysis" items={analysisNav} collapsible resolveAccess={resolveProjectAccess} />
                {sidebarCollapsed ? (
                  <div className="w-8 h-px bg-slate-200 my-2" />
                ) : (
                  <Separator className="bg-slate-100" />
                )}
                <NavSection title="Operations" items={operationsNav} collapsible resolveAccess={resolveProjectAccess} />
                {sidebarCollapsed ? (
                  <div className="w-8 h-px bg-slate-200 my-2" />
                ) : (
                  <Separator className="bg-slate-100" />
                )}
                <NavSection title="Settings" items={settingsNav} collapsible resolveAccess={resolveProjectAccess} />
              </>
            )}

            {/* Admin Portal Link - Only visible to Admin/Super Admin */}
            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <>
                {sidebarCollapsed ? (
                  <div className="w-8 h-px bg-slate-200 my-2" />
                ) : (
                  <Separator className="bg-slate-100" />
                )}
                <div className={sidebarCollapsed ? 'flex flex-col items-center w-full' : 'space-y-1'}>
                  {!sidebarCollapsed && (
                    <p className="px-3 text-[10px] uppercase tracking-widest font-semibold text-slate-400 mb-2">
                      Administration
                    </p>
                  )}
                  {sidebarCollapsed ? (
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <NavLink
                            to="/admin"
                            className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                          >
                            <Shield className="h-5 w-5" />
                          </NavLink>
                        </TooltipTrigger>
                        <TooltipContent side="right" sideOffset={12} className="text-xs">
                          <p>Admin Portal</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <NavLink
                      to="/admin"
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                    >
                      <Shield className="h-4 w-4" />
                      Admin Portal
                    </NavLink>
                  )}
                </div>
              </>
            )}
          </nav>
        </ScrollArea>

        {/* User section at bottom */}
        <div className={`border-t border-slate-200 ${sidebarCollapsed ? 'py-3 flex flex-col items-center gap-2 px-[13px]' : 'p-3'}`}>
          {sidebarCollapsed ? (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <NavLink to="/profile" className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
                    <User className="h-5 w-5 text-slate-600" />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={12} className="text-xs">
                  <p>{user?.name}</p>
                  <p className="text-slate-400">{formatRole(user?.role)}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                    onClick={handleLogout}
                    data-testid="logout-button"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={12} className="text-xs">
                  <p>Sign out</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <>
              <NavLink
                to="/profile"
                className="flex items-center gap-3 mb-2 px-2 rounded-md hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100">
                  <User className="h-4 w-4 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate" data-testid="user-name">
                    {user?.name}
                  </p>
                  <p className="text-[10px] text-slate-400 truncate" data-testid="user-role">
                    {formatRole(user?.role)}
                  </p>
                </div>
              </NavLink>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2 text-slate-500 hover:text-slate-900 hover:bg-slate-50 h-8"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </Button>
            </>
          )}
        </div>
      </aside>

      {/* Main content — full width below md, offset by the sidebar at md+ */}
      <main className={`transition-all duration-300 ml-0 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        {/* Top header */}
        <header className="sticky top-0 z-30 h-14 border-b border-slate-200 bg-white">
          <div className="flex h-full items-center justify-between px-6">
            {/* Project context - Active Project Header (display only, hidden on dashboard) */}
            <div className="flex items-center gap-3">
              {/* Mobile-only hamburger to open the sidebar drawer */}
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden h-9 w-9 p-0"
                onClick={() => setMobileSidebarOpen(true)}
                data-testid="mobile-sidebar-toggle"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5 text-slate-600" />
              </Button>
              {/* Project context is shown in the mobile view's own header, so this
                  desktop chrome is hidden below md to keep the bar from overflowing. */}
              {showProjectSelector && (
                <div className="hidden md:flex items-center min-w-0">
                  <ActiveProjectHeader />
                </div>
              )}
            </div>
            {/* User info and notifications */}
            <div className="flex items-center gap-4">
              {/* Notification Bell */}
              <Button
                variant="ghost"
                size="sm"
                className="relative h-9 w-9 p-0"
                onClick={() => navigate('/notifications')}
                data-testid="notification-bell"
              >
                <Bell className="h-5 w-5 text-slate-500" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Button>
              <span className="hidden md:inline-block text-xs px-2 py-1 rounded bg-slate-100 text-slate-500" data-testid="role-badge">
                {formatRole(user?.role)}
              </span>
              <span className="hidden md:inline text-sm text-slate-500" data-testid="user-email">
                {user?.email}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-6">
          <ErrorBoundary variant="section" name="dashboard-page" resetKeys={[location.pathname]}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>

      {/* AI Assistant Floating Widget — isolated so a widget crash never takes down the layout */}
      <ErrorBoundary name="ai-widget" fallback={() => null}>
        <AIAssistantWidget />
      </ErrorBoundary>
    </div>
  );
};

export default DashboardLayout;
